import { Box, Group, Popover, Text, useMantineTheme } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { IconCalendar } from '@tabler/icons-react';
import { TaskBadge } from 'components/TaskBadge';
import dayjs from 'dayjs';
import { useUpdateTask } from 'modules/Tasks/api/use-update-task';
import { useState } from 'react';
import type { Task } from 'shared/hooks/use-load-tasks';

type EditableDueDateBadgeProps = {
  task: Task;
};

export const EditableDueDateBadge = ({ task }: EditableDueDateBadgeProps) => {
  const theme = useMantineTheme();
  const { updateTask } = useUpdateTask();

  const [isPopoverOpened, setIsPopoverOpened] = useState(false);

  const handleChange = (value: string | null) => {
    if (!value) return;

    updateTask(
      { id: task.id, data: { dueDate: value } },
      { onSettled: () => setIsPopoverOpened(false) },
    );
  };

  return (
    <Popover
      opened={isPopoverOpened}
      onChange={setIsPopoverOpened}
      position="bottom-start"
      withinPortal
    >
      <Popover.Target>
        <Box onClick={e => e.stopPropagation()}>
          <TaskBadge
            icon={<IconCalendar size={18} stroke={1.5} color={theme.colors.purple[5]} />}
            onClick={() => setIsPopoverOpened(o => !o)}
          >
            <Group gap={4}>
              <Text size="sm">{task.due_date?.format('L')}</Text>
              {(task.days_postponed ?? 0) > 0 && (
                <Text c="warning.6" size="xs">{`+${task.days_postponed}`}</Text>
              )}
            </Group>
          </TaskBadge>
        </Box>
      </Popover.Target>
      <Popover.Dropdown p="xs" onClick={e => e.stopPropagation()}>
        <DatePicker
          minDate={dayjs().toDate()}
          value={task.due_date?.toISOString() ?? null}
          onChange={handleChange}
        />
      </Popover.Dropdown>
    </Popover>
  );
};
